import { useState, useEffect, useMemo } from 'react'
import {
  FaGithub, FaLinkedin, FaBrain, FaCode, FaGamepad, FaShieldAlt,
  FaArrowRight, FaPlay, FaTrophy, FaStar, FaMapMarkerAlt, FaChevronDown
} from 'react-icons/fa'
import { useTheme } from '../contexts/ThemeContext'
import { useTranslation } from 'react-i18next'

// Ordered to match the site-wide flow: AI → Full-Stack → Game Dev → Security
const DISCIPLINES = [
  { key: 'ai',       icon: FaBrain,     href: '#projects' },
  { key: 'fullstack', icon: FaCode,     href: '#projects' },
  { key: 'gamedev',  icon: FaGamepad,   href: '#projects' },
  { key: 'security', icon: FaShieldAlt, href: '#resume' },
] as const;

const STATS = [
  { key: 'projects', value: '20+' },
  { key: 'years',    value: '6+' },
  { key: 'games',    value: '10+' },
  { key: 'jss',      value: '100%' },
] as const;

const Hero = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isModern = theme === 'modern';
  const [currentRole, setCurrentRole] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);

  const defaultRoles = useMemo(() => {
    const roles = t('hero.roles.default', { returnObjects: true });
    return Array.isArray(roles) ? (roles as string[]) : [];
  }, [t]);
  const shortRoles = useMemo(() => {
    const roles = t('hero.roles.short', { returnObjects: true });
    return Array.isArray(roles) ? (roles as string[]) : [];
  }, [t]);
  const responsiveRoles = useMemo(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 480) {
      return shortRoles;
    }
    return defaultRoles;
  }, [defaultRoles, shortRoles]);

  // Typing animation effect
  useEffect(() => {
    if (responsiveRoles.length === 0) {
      setDisplayText('');
      return;
    }

    const roleIndex = currentRole % responsiveRoles.length;
    const currentWord = responsiveRoles[roleIndex] ?? '';
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < currentWord.length) {
          setDisplayText(currentWord.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (charIndex > 0) {
          setDisplayText(currentWord.slice(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          setIsDeleting(false);
          setCurrentRole((prev) => (prev + 1) % responsiveRoles.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, currentRole, responsiveRoles]);

  useEffect(() => {
    setCurrentRole(0);
    setDisplayText('');
    setCharIndex(0);
    setIsDeleting(false);
  }, [defaultRoles, shortRoles]);

  const portrait = isModern ? '/hero-modern.jpg' : '/hero-classic.png';

  return (
    <section id="hero" className={`hx ${isModern ? 'hx--modern' : 'hx--classic'}`}>
      {/* Background layers */}
      {isModern ? (
        <div className="hx-bg" aria-hidden="true">
          <div className="absolute inset-0 sf-hero-bg"></div>
          <div className="absolute inset-0 sf-hero-grid"></div>
          <div className="hx-aurora hx-aurora--a"></div>
          <div className="hx-aurora hx-aurora--b"></div>
          <div className="hx-aurora hx-aurora--c"></div>
          <div className="absolute inset-x-0 h-px sf-scan-beam"></div>
          <div className="absolute inset-0 sf-hero-vignette"></div>
        </div>
      ) : (
        <div className="hx-bg" aria-hidden="true">
          <div className="absolute inset-0 nb-hero-base"></div>
          <div className="absolute inset-0 nb-hero-halftone"></div>
          <div className="absolute top-0 right-0 w-1/2 h-1/2 nb-hero-stripes"></div>
          <div className="absolute bottom-0 left-0 w-2/5 h-2/5 nb-hero-stripes nb-hero-stripes--alt"></div>
        </div>
      )}

      <div className="hx-inner">
        {/* ============== COPY ============== */}
        <div className="hx-copy">
          <div className="hx-status">
            <span className="hx-status__dot" aria-hidden="true"></span>
            <span>{t('hero.availability')}</span>
            <span className="hx-status__sep" aria-hidden="true">·</span>
            <FaMapMarkerAlt className="hx-status__pin" aria-hidden="true" />
            <span className="hx-status__where">{t('hero.openAnywhere')}</span>
          </div>

          <p className="hx-greet">{t('hero.greeting')}</p>
          <h1 className="hx-name">
            <span className="hx-name__first">{t('hero.name').split(' ')[0]}</span>{' '}
            <span className="hx-name__last">{t('hero.name').split(' ').slice(1).join(' ')}</span>
          </h1>

          <div className="hx-role" aria-live="polite">
            <span className="hx-role__prompt" aria-hidden="true">{isModern ? '>' : '→'}</span>
            <span className="hx-role__text">{displayText || ' '}</span>
            <span className="hx-role__cursor" aria-hidden="true"></span>
          </div>

          <ul className="hx-disciplines">
            {DISCIPLINES.map(({ key, icon: Icon, href }, i) => (
              <li key={key}>
                <a href={href} className={`hx-chip hx-chip--${key}`} style={{ animationDelay: `${0.35 + i * 0.08}s` }}>
                  <Icon className="hx-chip__icon" aria-hidden="true" />
                  <span>{t(`hero.disciplines.${key}`)}</span>
                </a>
              </li>
            ))}
          </ul>

          <p className="hx-desc">{t('hero.description')}</p>

          <div className="hx-actions">
            <a href="#contact" className="hx-btn hx-btn--primary">
              <span>{t('hero.cta')}</span>
              <FaArrowRight aria-hidden="true" />
            </a>
            <a href="#projects" className="hx-btn hx-btn--ghost">
              <span>{t('hero.ctaSecondary')}</span>
            </a>
            <div className="hx-socials">
              <a href="https://github.com/motirebuma" target="_blank" rel="noopener noreferrer" className="hx-social" aria-label="GitHub">
                <FaGithub />
              </a>
              <a href="https://linkedin.com/in/motirebuma" target="_blank" rel="noopener noreferrer" className="hx-social" aria-label="LinkedIn">
                <FaLinkedin />
              </a>
            </div>
          </div>

          <dl className="hx-stats">
            {STATS.map(({ key, value }) => (
              <div key={key} className="hx-stat">
                <dt className="hx-stat__label">{t(`hero.stats.${key}`)}</dt>
                <dd className="hx-stat__value">{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* ============== VISUAL ============== */}
        <div className="hx-visual">
          <div className="hx-portrait">
            {isModern && (
              <>
                <span className="hx-ring hx-ring--outer" aria-hidden="true"></span>
                <span className="hx-ring hx-ring--inner" aria-hidden="true"></span>
              </>
            )}
            {!isModern && <span className="hx-portrait__tape" aria-hidden="true"></span>}
            <div className="hx-portrait__frame">
              <img src={portrait} alt={t('hero.imageAlt')} className="hx-portrait__img" />
            </div>
            {!isModern && <span className="hx-portrait__caption">M. REBUMA · AI · DEV · GAMES · SEC</span>}
          </div>

          <div className="hx-badge hx-badge--rated">
            <FaStar className="hx-badge__icon" aria-hidden="true" />
            <div>
              <strong>{t('hero.badges.topRated')}</strong>
              <span>{t('hero.badges.topRatedSub')}</span>
            </div>
          </div>

          <div className="hx-badge hx-badge--ctf">
            <FaTrophy className="hx-badge__icon" aria-hidden="true" />
            <div>
              <strong>{t('hero.badges.ctf')}</strong>
              <span>{t('hero.badges.ctfSub')}</span>
            </div>
          </div>

          <a href="/games/neon-drift/" target="_blank" rel="noopener noreferrer" className="hx-game">
            <img src="/projects/neon-drift/1.jpg" alt="" className="hx-game__thumb" />
            <div className="hx-game__meta">
              <span className="hx-game__eyebrow">{t('hero.badges.nowPlaying')}</span>
              <strong>Neon Drift</strong>
            </div>
            <span className="hx-game__play" aria-label={t('projects.play')}>
              <FaPlay aria-hidden="true" />
            </span>
          </a>
        </div>
      </div>

      <a href="#about" className="hx-scroll" aria-label={t('hero.scroll')}>
        <span>{t('hero.scroll')}</span>
        <FaChevronDown aria-hidden="true" />
      </a>
    </section>
  )
}

export default Hero;
